import { useQuery } from "react-query";
import _isEmpty from "lodash/isEmpty";
import {
  GET_GAME_CONFIG,
  TOURNAMENT_LIST_PUBLIC,
  TOURNAMENTS_LIST,
} from "../appConstants/APIEndpoint";
import { requestApiGet, requestApiPost } from "./index";
import type { IToken, PreSignUpConfig } from "../storage/store";
import { onPreSignupSuccess } from "../services/AuthService";
import {
  HomeScreenConfig,
  TournamentData,
  TournamentsConfig,
  Widget,
} from "../interfaces/TournamentsConfig";
import { useTokenStore } from "../storage/tokenState";
import { useMemo } from "react";
import { GameConfigs } from "../interfaces/GameConfig";
import { getCurrentTimestamp } from "../utils/DateUtils";
import { maxDiff, minDiff } from "../utils/helpers/tournamentConstants";
import { dispatchHomeScreenConfig } from "../storage/configState";
import { getPartnerId } from "../utils/helpers/common";

// Private function to get tournaments list
export const getTournamentsList = async (userToken: IToken) => {
  try {
    const { accessToken } = userToken;
    const currDate = getCurrentTimestamp();
    const maxTime = currDate + maxDiff;
    const minTime = currDate - minDiff;
    const partnerId = getPartnerId();
    const para = {
      partnerId: partnerId,
      limit: "50",
      maxTime: maxTime,
      minTime: minTime,
      gameName: "ludonx",
      // registeredTournaments: "123,456",
    };
    const config = { headers: { Authorization: accessToken }, params: para };
    const res = await requestApiGet<TournamentsConfig>(
      TOURNAMENTS_LIST,
      config
    );
    const response = res?.response;
    if (response?.success) {
      const tournamentConfig = response?.data;
      const downloadScreenData = tournamentConfig?.downloadScreenData;
      const helpScreenData = tournamentConfig?.helpScreenData;
      dispatchHomeScreenConfig({
        downloadScreenData,
        helpScreenData,
      } as HomeScreenConfig);
      return tournamentConfig;
    } else {
      throw new Error(
        JSON.stringify({
          type: response?.errorType,
          message: response?.externalMessage || response?.noResultText,
        })
      );
    }
  } catch (error) {
    console.error("Error in getTournamentsList:", error);
    throw error; // Re-throw to be handled by react-query
  }
};

// Public function to get tournaments list
const getPublicTournamentsList = async () => {
  try {
    const currDate = getCurrentTimestamp();
    const maxTime = currDate + maxDiff;
    const minTime = currDate - minDiff;
    const partnerId = getPartnerId();
    const para = {
      partnerId: partnerId,
      limit: "50",
      minTime: maxTime,
      maxTime: minTime,
      gameName: "ludonx",
      // registeredTournaments: "123,456",
    };
    const res = await requestApiGet<TournamentsConfig>(TOURNAMENT_LIST_PUBLIC, {
      params: para,
    });
    const response = res?.response;
    if (response?.success) {
      const tournamentConfig = response?.data;
      const downloadScreenData = tournamentConfig?.downloadScreenData;
      const helpScreenData = tournamentConfig?.helpScreenData;
      dispatchHomeScreenConfig({
        downloadScreenData,
        helpScreenData,
      } as HomeScreenConfig);
      return tournamentConfig;
    } else {
      // Throw error to prevent retries
      throw new Error(
        response?.noResultText || "Failed to get public tournament list"
      );
    }
  } catch (error) {
    console.error("Error in getPublicTournamentsList:", error);
    throw error; // Re-throw to be handled by react-query
  }
};

export const useTournamentsListApi = () => {
  const { token: userToken } = useTokenStore();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["tournamentsListApi", userToken],
    queryFn: () =>
      _isEmpty(userToken)
        ? getPublicTournamentsList()
        : getTournamentsList(userToken),
    onError: (err) => {
      console.error("Error in useTournamentsListApi:", err);
    },
    retry: false, // Disable retries
  });

  const tournamentsConfig = useMemo(
    () => data as unknown as TournamentData,
    [data]
  );

  return {
    tournamentsConfig,
    isLoading,
    error,
    refetch,
  };
};
